import React, { useState, useRef, useEffect } from 'react';
import { X, Sparkles, Copy, Check, Send } from 'lucide-react';
import { crmApi } from '../services/crmApi';

interface AIAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateContext?: string;
  applicationId?: number;
  apiKey?: string; // Deprecated - kept for backward compatibility
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const QUICK_PROMPTS = [
  // 📧 KOMUNIKACJA EMAILOWA
  '📧 Napisz email powitalny dla nowego kandydata',
  '📧 Napisz email przypominający o brakujących dokumentach',
  '📧 Napisz email z prośbą o uzupełnienie list intencyjnych',
  '📧 Napisz email z gratulacjami po akceptacji wniosku',
  '📧 Napisz email informujący o odrzućeniu wniosku (z uzasadnieniem)',
  '📧 Napisz email z przypomnieniem o terminie rozliczenia',
  '📧 Napisz email z prośbą o wyjaśnienie wątpliwości',
  '📧 Napisz email z zaproszeniem na spotkanie/konsultację',

  // 📋 DOKUMENTY & CHECKLISTY
  '📋 Przygotuj szczegółowy checklist dokumentów do zebrania',
  '📋 Lista dokumentów dla tytułu prawnego do lokalu',
  '📋 Wymagane certyfikaty i zaświadczenia',
  '📋 Dokumenty finansowe - faktury, rachunki, umowy',
  '📋 Checklist przed złożeniem wniosku do PUP',
  '📋 Dokumenty zabezpieczenia zwrotu (weksel/blokada)',

  // 🎯 PUNKTACJA & KWALIFIKACJA
  '🎯 Zasugeruj jak poprawić punktację kandydata (konkretne działania)',
  '🎯 Przeanalizuj punktację szczegółowo (wszystkie kategorie)',
  '🎯 Jakie kursy dałyby dodatkowe punkty?',
  '🎯 Czy kandydat należy do grup preferencyjnych?',
  '🎯 Jak zdobyć brakujące 2-3 punkty?',
  '🎯 Weryfikacja zgodności wykształcenia z branżą',

  // ⚠️ RYZYKA & OSTRZEŻENIA
  '⚠️ Jakie są największe ryzyka tego wniosku?',
  '⚠️ Zidentyfikuj wszystkie błędy dyskwalifikujące',
  '⚠️ Sprawdź compliance z regulaminem PUP 2026',
  '⚠️ Analiza ryzyka braku zabezpieczenia zwrotu',
  '⚠️ Potencjalne problemy z rozliczeniem wydatków',
  '⚠️ Ostrzeżenia dot. tytułu prawnego do lokalu',

  // 💼 ANALIZA BIZNESOWA
  '💼 Oceń realność pomysłu biznesowego',
  '💼 Analiza konkurencji w branży kandydata',
  '💼 Potencjał rynkowy - szanse i zagrożenia',
  '💼 Czy PKD jest odpowiedni dla tego biznesu?',
  '💼 Analiza lokalizacji działalności',
  '💼 Sugestie rozwoju biznesu po 12 miesiącach',
  '💼 Prognozy finansowe - przychody i koszty',

  // 💰 PLANOWANIE FINANSOWE
  '💰 Weryfikacja planowanych wydatków (czy kwalifikowalne?)',
  '💰 Optymalizacja listy zakupów (cena vs jakość)',
  '💰 Czy kwota 35000 PLN jest realistyczna?',
  '💰 Plan wydatków na pierwsze 2 miesiące',
  '💰 Rozliczenie VAT - co kandydat musi wiedzieć',
  '💰 Zabezpieczenie zwrotu: weksel vs blokada rachunku',

  // 📜 PRAWNE & ZGODNOŚĆ
  '📜 Weryfikacja wymagań bezwzględnych (IPD, dotacje, działalność)',
  '📜 Tytuł prawny do lokalu - czy wystarczający?',
  '📜 Analiza umowy najmu/użyczenia lokalu',
  '📜 UWAGA: Ten sam adres co inna firma - czy problem?',
  '📜 Obowiązki kandydata przez 12 miesięcy prowadzenia',
  '📜 Konsekwencje nieprawidłowego rozliczenia',

  // 📍 LOKALIZACJA & KONKURENCJA
  '📍 Analiza lokalizacji (dostępność, target, ruch)',
  '📍 Konkurencja w okolicy - mocne/słabe strony',
  '📍 Czy Kraków to dobra lokalizacja dla tego biznesu?',
  '📍 Rekomendacje: jak się wyróżnić lokalnie?',
  '📍 Luki rynkowe w tej dzielnicy/okolicy',

  // 🎓 KURSY & SZKOLENIA
  '🎓 Które kursy Fundacji NH są najlepsze dla tego kandydata?',
  '🎓 Jakie certyfikaty podniosą wartość wniosku?',
  '🎓 Plan rozwoju kompetencji kandydata (3-6 miesięcy)',
  '🎓 Czy doświadczenie kandydata jest wystarczające?',

  // 📄 GENEROWANIE TREŚCI
  '📄 Napisz szczegółowy opis planowanej działalności (300-500 słów)',
  '📄 Napisz uzasadnienie wyboru lokalizacji',
  '📄 Napisz plan marketingowy dla tego biznesu',
  '📄 Napisz analizę konkurencji (150-200 słów)',
  '📄 Napisz krótkie podsumowanie wniosku dla kierownika',
  '📄 Napisz notatkę do akt dotyczącą tego kandydata',

  // 🔍 PYTANIA & WYJAŚNIENIA
  '🔍 Zaproponuj 5-10 pytań do wyjaśnienia z kandydatem',
  '🔍 Co należy dopytać o doświadczenie zawodowe?',
  '🔍 Pytania o szczegóły planowanych zakupów',
  '🔍 Wyjaśnienie wątpliwości dot. tytułu prawnego',

  // ✅ AKCJE NASTĘPNE (FOLLOW-UP)
  '✅ Przygotuj plan działań dla kandydata (krok po kroku)',
  '✅ Lista zadań do wykonania przed złożeniem wniosku',
  '✅ Harmonogram zbierania dokumentów (timeline)',
  '✅ Co zrobić jeśli wniosek zostanie odrzucony?',
  '✅ Checklist po otrzymaniu dotacji (first 30 days)',

  // 📊 RAPORTY & ANALIZY
  '📊 Raport SWOT dla tego biznesu',
  '📊 Analiza szans powodzenia (scoring 1-10)',
  '📊 Porównanie z innymi wnioskami (benchmark)',
  '📊 Rekomendacja: zatwierdzić czy odrzućić? (z uzasadnieniem)',

  // 🎯 OPTYMALIZACJA WNIOSKU
  '🎯 Top 3 rzeczy do poprawy w tym wniosku',
  '🎯 Jak zwiększyć szanse na akceptację o 20-30%?',
  '🎯 Quick wins - co poprawić w 24-48h?',
  '🎯 Strategia długoterminowa dla kandydata (6-12 miesięcy)',

  // 🧠 DORADZTWO SPECJALISTYCZNE
  '🧠 Doradztwo prawne: umowy, zobowiązania, ryzyko',
  '🧠 Doradztwo finansowe: budżet, prognozy, VAT',
  '🧠 Doradztwo marketingowe: pozycjonowanie, reklama',
  '🧠 Doradztwo operacyjne: procesy, zakupy, dostawcy'
];

export const AIAssistantModal: React.FC<AIAssistantModalProps> = ({
  isOpen,
  onClose,
  candidateContext = '',
  applicationId,
  apiKey // Deprecated
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (prompt: string) => {
    if (!prompt.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: prompt };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const systemPrompt = `Jesteś ekspertem ds. dotacji z Zespołu Ds. Dotacji Fundacji Promocji Nowej Huty. Pomagasz operatorom w obsłudze wniosków o dotację na rozpoczęcie działalności gospodarczej w 2026 roku.

## KLUCZOWE INFORMACJE 2026:
- **Maksymalna kwota dotacji: 35 000,00 PLN**
- **Minimum punktów do zakwalifikowania: 23 punkty**
- **Okres prowadzenia działalności: 12 miesięcy obowiązkowych**
- **Rozliczenie wydatków: 2 miesiące od rozpoczęcia**

## WYMAGANIA BEZWZGLĘDNE (brak = odrzućenie):
✅ Zarejestrowanie w Fundacja Promocji Nowej Huty z IPD (Indywidualny Plan Działania) z zapisem o dotacji
✅ NIE otrzymano nigdy wcześniej dotacji na działalność (nawet 20 lat temu!)
✅ NIE prowadzono działalności w ostatnich 12 miesiącach
✅ NIE odmówiono pracy w ostatnich 12 miesiącach
✅ Tytuł prawny do lokalu na minimum 12 miesięcy (własność/najem/użyczenie)
✅ NIE można rozpocząć pod tym samym adresem i PKD co istniejąca firma (bez pisemnego uzasadnienia)

## CO MOŻNA FINANSOWAĆ:
✅ Środki trwałe, maszyny, urządzenia
✅ Wynajem lokalu (max 3x miesięczny czynsz)
✅ Samochód ciężarowy (max 50% kwoty dotacji, NIE do transportu towarów)
✅ Materiały do produkcji, towary NOWE do sprzedaży
✅ Adaptacja lokalu, strona www, materiały reklamowe
✅ Pomoc prawna i doradztwo związane z rozpoczęciem

## CZEGO NIE MOŻNA FINANSOWAĆ:
❌ Podatki, ubezpieczenia, koncesje, ZUS
❌ Bieżące koszty (media, telefon, paliwo)
❌ Leasing, kredyt, zakup w ratach
❌ Zakupy od rodziny (współmałżonek, rodzice, dzieci, rodzeństwo)
❌ Transport towarów, przewóz osób
❌ Lombardy, salony gier, agencje towarzyskie
❌ Handel alkoholem, używkami, bronią
❌ Wynajem nieruchomości, chów zwierząt
❌ Ten sam adres i PKD co inna firma (bez uzasadnienia)

## SYSTEM PUNKTOWY (minimum 23 pkt):
- Produkcja: +2 pkt (usługi/handel: +1 pkt)
- Wykształcenie zgodne z branżą: +4 pkt (podobne: +2 pkt)
- Kursy z certyfikatami: max +3 pkt (3 kursy=+3, 2=+2, 1=+1)
- Doświadczenie: >3 lata: +4 pkt, 1-3 lata: +3 pkt, 6-12m: +2 pkt
- Kraków: +1 pkt
- Grupy preferencyjne: max +2 pkt (długotrwale bezrobotny, >50 lat, <30 lat, niepełnosprawny, samotny rodzic, bez kwalifikacji)

## ZABEZPIECZENIE ZWROTU:
1. **Weksel z poręczeniem (aval)** - poręczyciel min. 5 600 PLN brutto dochodu (umowa na czas nieokreślony lub min. 18m), do 80 roku życia, bez postępowań egzekucyjnych
2. **Blokada rachunku** - 35 000 PLN + 9,25% odsetki = **38 238 PLN** zablokowane na czas nieokreślony

## ZOBOWIĄZANIA PO OTRZYMANIU:
- Prowadzić działalność przez 12 miesięcy (bez zawieszenia >6 miesięcy)
- NIE podejmować zatrudnienia przez 12 miesięcy
- Rozliczyć w 2 miesiące - faktury VAT na NIP firmy, brutto
- Zwrócić odliczony VAT (w 30 dni od zwrotu lub 90 dni od deklaracji)
- Rzeczy używane minimum 1000 PLN brutto wartość

## NAJCZĘSTSZE BŁĘDY:
🚫 Brak IPD z zapisem o dotacji
🚫 Poprzednie otrzymanie dotacji (nawet dawno temu)
🚫 Prowadzenie działalności w ostatnich 12 miesiącach
🚫 Brak tytułu prawnego do lokalu na 12 miesięcy
🚫 Ten sam adres i PKD co istniejąca firma
🚫 Wydatki niezwiązane z działalnością
🚫 Zakupy od rodziny
🚫 Samochód powyżej 50% kwoty

${candidateContext ? `\n## KONTEKST BIEŻĄCEGO KANDYDATA:\n${candidateContext}\n` : ''}

Odpowiadaj konkretnie, profesjonalnie i po polsku. Sprawdźaj czy kandydat spełnia wymagania według powyższych zasad. Jeśli piszesz email, użyj odpowiedniego formatowania. Zawsze odwołuj się do konkretnych zasad z powyższych informacji.`;

      // Use backend API instead of direct Anthropic call
      if (!applicationId) {
        throw new Error('Application ID is required for AI chat');
      }

      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const responseText = await crmApi.aiChat(applicationId, prompt, history);

      const assistantMessage: Message = {
        role: 'assistant',
        content: responseText
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error calling Claude API:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Przepraszam, wystąpił błąd podczas komunikacji z AI. Sprawdź klucz API lub spróbuj ponownie.'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    sendMessage(prompt);
  };

  const handleSend = () => {
    if (inputValue.trim()) {
      sendMessage(inputValue);
    }
  };

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center">
            <Sparkles className="mr-3" size={28} />
            <div>
              <h2 className="text-2xl font-bold">Asystent AI</h2>
              <p className="text-purple-100 text-sm">Pomoc w obsłudze wniosków</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-purple-200 transition-colors"
          >
            <X size={28} />
          </button>
        </div>

        {/* Quick Prompts */}
        <div className="p-4 bg-gray-50 border-b overflow-x-auto">
          <p className="text-sm font-semibold text-gray-700 mb-2">Szybkie akcje:</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_PROMPTS.map((prompt, idx) => (
              <button
                key={idx}
                onClick={() => handleQuickPrompt(prompt)}
                disabled={isLoading}
                className="px-3 py-1.5 bg-white border border-purple-300 rounded-lg text-sm text-purple-700 hover:bg-purple-50 hover:border-purple-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-gray-400 py-12">
              <Sparkles size={48} className="mx-auto mb-4 opacity-30" />
              <p>Rozpocznij rozmowę lub wybierz szybką akcję</p>
            </div>
          )}

          {messages.map((message, idx) => (
            <div
              key={idx}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg p-4 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <div className="whitespace-pre-wrap break-words">{message.content}</div>
                {message.role === 'assistant' && (
                  <button
                    onClick={() => copyToClipboard(message.content, idx)}
                    className="mt-2 flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {copiedIndex === idx ? (
                      <>
                        <Check size={14} className="mr-1" />
                        Skopiowano
                      </>
                    ) : (
                      <>
                        <Copy size={14} className="mr-1" />
                        Kopiuj
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 bg-gray-50 border-t rounded-b-xl">
          <div className="flex items-center gap-2">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Wpisz pytanie lub prośbę... (Enter = wyślij, Shift+Enter = nowa linia)"
              disabled={isLoading}
              rows={1}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !inputValue.trim()}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
            >
              <Send size={20} className="mr-2" />
              Wyślij
            </button>
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                disabled={isLoading}
                className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Wyczyść
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
